import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, FormGroup,
    Label, Input, Button, Row, Col, InputGroup, Table
} from 'reactstrap';
import Swal from 'sweetalert2';
import Select from 'react-select';

import { TerceiroContext } from '../../context/TerceiroContext';
import { GerenteFotografoContext } from '../../context/GerenteFotografoContext';
import { TipoTerceiroContext } from '../../context/TipoTerceiro';
import { EditTerceiroType, TerceiroType } from '../../context/hooks/useTerceiro';
import { AuthContext } from '../../context/AuthContext';
import { TipoServicoContext } from '../../context/TipoServicoContext';
import { AgendaContext } from '../../context/AgendaContext';
import { CurrencyInput } from '../Form/CurrencyInput';

import useUser from '../../context/hooks/useUser';
import CreateTipoTerceiroModal from '../TipoTerceiro/CreateTipoTerceiroModal';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    terceiro: EditTerceiroType | null;
    onUpdated?: () => void;
}

type ServicoVinculado = { tipoServicoId: number; nome: string; value: number };

export const EditTerceiroModal: React.FC<Props> = ({
    isOpen,
    toggle,
    terceiro,
    onUpdated
}) => {
    const { objUserTk } = useContext(AuthContext);

    const {
        updateTerceiro,
        listFotografos,
        handleGetFotografosByEmpresa,
        handleGetServicosByTerceiro,
        addServicoToTerceiro,
        updateValorServicoTerceiro,
        removeServicoFromTerceiro,
    } = useContext(TerceiroContext);
    const { checkConflitoHorarioFotografo } = useContext(AgendaContext);
    const { listTipoTerceiro, getAllTipoTerceiroPaged } = useContext(TipoTerceiroContext);
    const { listTipoServico, getAllTipoServicoPaged } = useContext(TipoServicoContext);
    const { createGerenteFotografo, handleGetFotografosByGerente } = useContext(GerenteFotografoContext);
    const { validateCpf } = useUser();

    const [formData, setFormData] = useState<EditTerceiroType | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedFotografos, setSelectedFotografos] = useState<number[]>([]);
    const [fotografosVinculados, setFotografosVinculados] = useState<TerceiroType[]>([]);

    const [servicosVinculados, setServicosVinculados] = useState<ServicoVinculado[]>([]);
    const [valorModalOpen, setValorModalOpen] = useState(false);
    const [servicoEmEdicao, setServicoEmEdicao] = useState<{ tipoServicoId: number; nome: string } | null>(null);
    const [valorInput, setValorInput] = useState<number>(0);

    useEffect(() => {
        if (!isOpen || !terceiro) return;

        setFormData(terceiro);
        setSelectedFotografos([]);
        setFotografosVinculados([]);
        setServicosVinculados([]);

        getAllTipoTerceiroPaged(1, 1000, objUserTk.EmpresaId);
        getAllTipoServicoPaged(1, 1000, objUserTk.EmpresaId);
        handleGetFotografosByEmpresa(objUserTk.EmpresaId);

        handleGetServicosByTerceiro(terceiro.id).then((res: any) => {
            if (Array.isArray(res)) {
                const mapped = res.map((r: any) => ({
                    tipoServicoId: r.servicoId ?? r.tipoServicoId ?? r.id,
                    nome: r.servicoName ?? r.nome ?? r.name,
                    value: Number(r.value ?? 0),
                })) as ServicoVinculado[];
                setServicosVinculados(mapped);
            }
        });

        const tipo = listTipoTerceiro.find(t => t.id === terceiro.tipoTerceiroId)?.type.toLowerCase();
        if (terceiro.isManager && tipo === 'fotógrafo') {
            handleGetFotografosByGerente(terceiro.id).then((res) => {
                if (res?.length) setFotografosVinculados(res);
            });
        }
    }, [isOpen, terceiro]);

    useEffect(() => {
        if (!formData) return;
        const tipo = listTipoTerceiro.find(t => t.id === formData.tipoTerceiroId)?.type.toLowerCase();
        const isFotografo = tipo === 'fotógrafo';

        if (formData.isManager && isFotografo) {
            handleGetFotografosByGerente(formData.id!)
                .then(res => {
                    if (res?.length) setSelectedFotografos(res.map(f => f.id));
                });
        }
    }, [formData, listTipoTerceiro]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (!formData) return;
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: name === 'tipoTerceiroId' ? Number(value) : value });
    };

    const handleValidateCpf = async () => {
        if (!formData) return;
        const rawCpf = formData.CPF?.replace(/\D/g, '') ?? '';
        if (!rawCpf || rawCpf.length !== 11) return;

        const result = await validateCpf(rawCpf);
        if (result?.tipo) {
            const confirm = await Swal.fire({
                icon: 'warning',
                title: 'CPF já cadastrado!',
                html: `<b>Tipo:</b> ${result.tipo}<br/><b>Nome:</b> ${result.nome}<br/><br/>Deseja continuar mesmo assim?`,
                showCancelButton: true,
                confirmButtonText: 'Continuar',
                cancelButtonText: 'Limpar CPF',
                reverseButtons: true
            });
            if (!confirm.isConfirmed) {
                setFormData({ ...formData, CPF: '' });
            }
        }
    };

    const isTipoVendedor = formData
        ? listTipoTerceiro.find(t => t.id === formData.tipoTerceiroId)?.type.toLowerCase() === 'vendedor'
        : false;

    const isTipoFotografo = formData
        ? listTipoTerceiro.find(t => t.id === formData.tipoTerceiroId)?.type.toLowerCase() === 'fotógrafo'
        : false;

    const fotografosDisponiveis = listFotografos.filter(f => !fotografosVinculados.some(v => v.id === f.id));

    const servicosUsados = useMemo(
        () => new Set(servicosVinculados.map(s => s.tipoServicoId)),
        [servicosVinculados]
    );

    const tipoServicoOptions = useMemo(() => {
        return listTipoServico
            .filter(s => !servicosUsados.has(s.id))
            .map(s => ({ value: s.id, label: s.name }));
    }, [listTipoServico, servicosUsados]);

    const onSelectServico = (opt: any) => {
        if (!opt || !formData) return;
        const { value, label } = opt;
        const svc = listTipoServico.find(s => s.id === value);
        const valorPadrao = svc?.value ?? 0;

        setServicoEmEdicao({ tipoServicoId: value, nome: label });
        setValorInput(valorPadrao);
        setValorModalOpen(true);
    };

    const confirmarIncluirServico = async () => {
        if (!formData || !servicoEmEdicao) return;

        const value = Number(valorInput) || 0;
        const ok = await addServicoToTerceiro({
            terceiroId: formData.id!,
            tipoServicoId: servicoEmEdicao.tipoServicoId,
            value,
        });

        if (ok) {
            setServicosVinculados(prev => [
                ...prev,
                { tipoServicoId: servicoEmEdicao.tipoServicoId, nome: servicoEmEdicao.nome, value }
            ]);
            fecharModalValor();
        }
    };

    const editarValorServico = (s: ServicoVinculado) => {
        setServicoEmEdicao({ tipoServicoId: s.tipoServicoId, nome: s.nome });
        setValorInput(s.value);
        setValorModalOpen(true);
    };

    const confirmarEditarValor = async () => {
        if (!formData || !servicoEmEdicao) return;

        const value = Number(valorInput) || 0;
        const ok = await updateValorServicoTerceiro({
            terceiroId: formData.id!,
            tipoServicoId: servicoEmEdicao.tipoServicoId,
            value,
        });

        if (ok) {
            setServicosVinculados(prev =>
                prev.map(s =>
                    s.tipoServicoId === servicoEmEdicao.tipoServicoId ? { ...s, value } : s
                )
            );
            fecharModalValor();
        }
    };

    const removerServico = async (tipoServicoId: number) => {
        if (!formData) return;
        const confirm = await Swal.fire({
            icon: 'warning',
            title: 'Remover serviço deste terceiro?',
            text: 'Esta ação não poderá ser desfeita.',
            showCancelButton: true,
            confirmButtonText: 'Remover',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
        });
        if (!confirm.isConfirmed) return;

        const ok = await removeServicoFromTerceiro({
            terceiroId: formData.id!,
            tipoServicoId,
        });

        if (ok) {
            setServicosVinculados(prev => prev.filter(s => s.tipoServicoId !== tipoServicoId));
        }
    };

    const fecharModalValor = () => {
        setValorModalOpen(false);
        setServicoEmEdicao(null);
        setValorInput(0);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData) return;

        const ok = await updateTerceiro(formData);

        if (ok) {
            if (formData.isManager && isTipoFotografo && selectedFotografos.length) {
                await createGerenteFotografo({
                    empresaId: objUserTk.EmpresaId,
                    gerenteId: formData.id!,
                    fotografoId: selectedFotografos,
                });
            }
            onUpdated?.();
            toggle();
        }
    };

    if (!formData) return null;

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="lg">
                <ModalHeader toggle={toggle}>Alterar informações do terceiro</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label className="form-control-label">Tipo de terceiro</Label>
                                    <InputGroup>
                                        <Input
                                            className="form-control-alternative"
                                            type="select"
                                            name="tipoTerceiroId"
                                            value={formData.tipoTerceiroId}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value={0}>Selecione…</option>
                                            {listTipoTerceiro.map(t => (
                                                <option key={t.id} value={t.id}>{t.type}</option>
                                            ))}
                                        </Input><Button
                                                style={{ background: '#585858', color: '#fff' }}
                                                onClick={() => setCreateModalOpen(true)}
                                                type="button"
                                            >
                                                +
                                            </Button></InputGroup>
                                </FormGroup>
                            </Col>
                        </Row>

                        {(isTipoVendedor || isTipoFotografo) && (
                            <Row>
                                <Col md="4">
                                    <FormGroup>
                                        <Label className="form-control-label" htmlFor="spendingCeiling">Teto de Gastos</Label>
                                        <Input
                                            className="form-control-alternative"
                                            type="text"
                                            name="spendingCeiling"
                                            id="spendingCeiling"
                                            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                                                .format(Number(formData.spendingCeiling || 0))}
                                            onChange={(e) => {
                                                const rawValue = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
                                                const numeric = parseFloat(rawValue);
                                                setFormData(prev => ({ ...prev!, spendingCeiling: isNaN(numeric) ? 0 : numeric }));
                                            }}
                                            placeholder="R$ 0,00"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md="4">
                                    <FormGroup>
                                        <Label className="form-control-label" htmlFor="balanceExpenses">Saldo Atual</Label>
                                        <Input
                                            className="form-control-alternative"
                                            type="text"
                                            name="balanceExpenses"
                                            id="balanceExpenses"
                                            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                                                .format(Number(formData.balanceExpenses || 0))}
                                            onChange={(e) => {
                                                const rawValue = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
                                                const numeric = parseFloat(rawValue);
                                                setFormData(prev => ({ ...prev!, balanceExpenses: isNaN(numeric) ? 0 : numeric }));
                                            }}
                                            placeholder="R$ 0,00"
                                        />
                                    </FormGroup>
                                </Col>

                                {isTipoFotografo && (
                                    <Col md="4">
                                        <FormGroup>
                                            <Label className="form-control-label" htmlFor="isManager">É Gerente?</Label>
                                            <Input
                                                className="form-control-alternative"
                                                type="select"
                                                name="isManager"
                                                id="isManager"
                                                value={formData.isManager ? 'true' : 'false'}
                                                onChange={(e) => setFormData({ ...formData, isManager: e.target.value === 'true' })}
                                            >
                                                <option value="false">Não</option>
                                                <option value="true">Sim</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>
                        )}

                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="name">Nome</Label>
                                    <Input
                                        className="form-control-alternative"
                                        id="name"
                                        name="name"
                                        value={formData.name || ''}
                                        onChange={handleChange}
                                        placeholder="Nome"
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="email">Email</Label>
                                    <Input
                                        className="form-control-alternative"
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email || ''}
                                        onChange={handleChange}
                                        placeholder="Email"
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="CPF">CPF</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="CPF"
                                        id="CPF"
                                        value={formData?.CPF || ''}
                                        placeholder="CPF"
                                        onChange={handleChange}
                                        onBlur={handleValidateCpf}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="telephone">Telefone</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="telephone"
                                        id="telephone"
                                        value={formData?.telephone || ''}
                                        placeholder="Telefone"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="telephone_01">Telefone 01</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="telephone_01"
                                        id="telephone_01"
                                        value={formData?.telephone_01 || ''}
                                        placeholder="Telefone 01"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="address">Endereço</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="address"
                                        id="address"
                                        value={formData?.address || ''}
                                        placeholder="Endereço"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="addressNumber">Número</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="addressNumber"
                                        id="addressNumber"
                                        value={formData?.addressNumber || ''}
                                        placeholder="Número"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="neighborhood">Bairro</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="neighborhood"
                                        id="neighborhood"
                                        value={formData?.neighborhood || ''}
                                        placeholder="Bairro"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="city">Cidade</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="city"
                                        id="city"
                                        value={formData?.city || ''}
                                        placeholder="Cidade"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="state">Estado</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="state"
                                        id="state"
                                        value={formData?.state || ''}
                                        placeholder="Estado"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="zipCode">CEP</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="zipCode"
                                        id="zipCode"
                                        value={formData?.zipCode || ''}
                                        placeholder="CEP"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="landline">Telefone Fixo</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="landline"
                                        id="landline"
                                        value={formData?.landline || ''}
                                        placeholder="Telefone Fixo"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="observation">Observação</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="textarea"
                                        name="observation"
                                        id="observation"
                                        value={formData?.observation || ''}
                                        placeholder="Observação"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="3">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="bank">Banco</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="bank"
                                        id="bank"
                                        value={formData?.bank || ''}
                                        placeholder="Banco"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="agency">Agência</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="agency"
                                        id="agency"
                                        value={formData?.agency || ''}
                                        placeholder="Agência"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="currentAccount">Conta Corrente</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="currentAccount"
                                        id="currentAccount"
                                        value={formData?.currentAccount || ''}
                                        placeholder="Conta Corrente"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="3">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="pix">Pix</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="pix"
                                        id="pix"
                                        value={formData?.pix || ''}
                                        placeholder="Pix"
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <hr className="my-3" />
                        <h5 className="mb-3">Serviços</h5>

                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label className="form-control-label">Adicionar serviço</Label>
                                    <Select
                                        className="form-control-alternative"
                                        options={tipoServicoOptions}
                                        onChange={onSelectServico}
                                        value={null}
                                        placeholder={listTipoServico.length ? 'Digite para buscar...' : 'Nenhum serviço cadastrado'}
                                        classNamePrefix="select"
                                        isDisabled={!listTipoServico.length}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="12">
                                <Table bordered responsive>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60%' }}>Serviço</th>
                                            <th style={{ width: '20%' }}>Valor</th>
                                            <th style={{ width: '20%' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    {/* <tbody>
                                        {servicosVinculados.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className="text-center text-muted">
                                                    Nenhum serviço vinculado.
                                                </td>
                                            </tr>
                                        ) : (
                                            servicosVinculados.map(s => (
                                                <tr key={s.tipoServicoId}>
                                                    <td>{s.nome}</td>
                                                    <td>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(s.value)}</td>
                                                    <td>
                                                        <Button size="sm" color="secondary" onClick={() => editarValorServico(s)}>
                                                            Editar valor
                                                        </Button>{' '}
                                                        <Button size="sm" color="danger" onClick={() => removerServico(s.tipoServicoId)}>
                                                            Remover
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody> */}
                                    <tbody>
                                        {servicosVinculados.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className="text-center text-muted">
                                                    Nenhum serviço vinculado.
                                                </td>
                                            </tr>
                                        ) : (
                                            servicosVinculados.map(s => (
                                                <tr key={s.tipoServicoId}>
                                                    <td>{s.nome}</td>
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            bsSize="sm"
                                                            value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(s.value)}
                                                            onChange={(e) => {
                                                                const raw = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
                                                                const numeric = parseFloat(raw);
                                                                setServicosVinculados(prev =>
                                                                    prev.map(item =>
                                                                        item.tipoServicoId === s.tipoServicoId
                                                                            ? { ...item, value: isNaN(numeric) ? 0 : numeric }
                                                                            : item
                                                                    )
                                                                );
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            color="success"
                                                            onClick={async () => {
                                                                const updated = servicosVinculados.find(v => v.tipoServicoId === s.tipoServicoId);
                                                                if (!updated || !formData) return;
                                                                await updateValorServicoTerceiro({
                                                                    terceiroId: formData.id!,
                                                                    tipoServicoId: updated.tipoServicoId,
                                                                    value: updated.value
                                                                });
                                                            }}
                                                        >
                                                            Salvar
                                                        </Button>{' '}
                                                        <Button
                                                            size="sm"
                                                            color="danger"
                                                            onClick={() => removerServico(s.tipoServicoId)}
                                                        >
                                                            Remover
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-3">
                            <Button style={{ background: '#585858', color: '#fff' }} type="submit">
                                Salvar alterações
                            </Button>
                            <Button
                                className="ml-2"
                                style={{ background: '#d9534f', color: '#fff' }}
                                onClick={toggle}
                                type="button"
                            >
                                Cancelar
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            <Modal isOpen={valorModalOpen} toggle={fecharModalValor} backdrop="static">
                <ModalHeader toggle={fecharModalValor}>
                    {servicoEmEdicao ? `Definir valor: ${servicoEmEdicao.nome}` : 'Definir valor'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label className="form-control-label">Valor (R$)</Label>
                        <CurrencyInput
                            name="value"
                            value={valorInput}
                            onValueChange={(v) => setValorInput(v ?? 0)}
                            placeholder="R$ 0,00"
                        />
                    </FormGroup>
                    <div className="d-flex justify-content-end">
                        <Button color="secondary" onClick={fecharModalValor} type="button">
                            Cancelar
                        </Button>
                        <Button
                            className="ml-2"
                            style={{ background: '#585858', color: '#fff' }}
                            onClick={servicosUsados.has(servicoEmEdicao?.tipoServicoId ?? -1) ? confirmarEditarValor : confirmarIncluirServico}
                            type="button"
                        >
                            Salvar
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <CreateTipoTerceiroModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                onCreated={() => getAllTipoTerceiroPaged(1, 9, objUserTk.EmpresaId)}
            />
        </>
    );
};

export default EditTerceiroModal;